// comps/ResizableSplitPanel.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import './resizableSplitPanel.css';

const MAX_HEIGHT = 85;
const MIN_HEIGHT = 15;

const clampPct = (v, min = MIN_HEIGHT, max = MAX_HEIGHT) => Math.min(max, Math.max(min, v));

const ResizableSplitPanel = ({
  children,
  initialMapHeight = '60%',
  onResize,     // optional: notify parent (e.g., mapView.resize())
  storageKey,   // optional: persist user setting
}) => {
  const [topChild, bottomChild] = React.Children.toArray(children);
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const dragging = useRef(false);

  const [mapHeight, setMapHeight] = useState(() => {
    if (storageKey) {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) return `${saved}%`;
    }
    return initialMapHeight;
  });

  const setPct = useCallback((pct) => {
    const clamped = clampPct(pct);
    setMapHeight(`${clamped}%`);
    onResize?.(clamped);
    if (storageKey) window.localStorage.setItem(storageKey, String(clamped));
  }, [onResize, storageKey]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    handleRef.current?.setPointerCapture(e.pointerId);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const totalH = rect.height;
    const pct = (offsetY / totalH) * 100;
    setPct(pct);
  }, [setPct]);

  const endDrag = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const up = () => endDrag();
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [endDrag]);

  const onHandleDoubleClick = () => setPct(50);

  const currentPct = parseFloat(String(mapHeight).replace('%', ''));

  return (
    <div
      ref={containerRef}
      className="split-container"
      style={{ position: 'relative', height: '100%', width: '100%' }}
    >
      {/* Top (map) */}
      <div className="split-top" style={{ height: mapHeight, overflow: 'hidden' }}>
        {topChild}
      </div>

      {/* Handle */}
      <div
        ref={handleRef}
        className="split-handle"
        role="separator"
        aria-orientation="horizontal"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onDoubleClick={onHandleDoubleClick}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') setPct(currentPct - 2);
          if (e.key === 'ArrowDown') setPct(currentPct + 2);
          if (e.key === 'Home') setPct(MIN_HEIGHT);
          if (e.key === 'End') setPct(MAX_HEIGHT);
        }}
        style={{
          height: 8,
          background: '#e0e0e0',
          borderTop: '1px solid #cfcfcf',
          borderBottom: '1px solid #cfcfcf',
          cursor: 'ns-resize'
        }}
        title="Drag to resize"
      />

      {/* Bottom (table) */}
      <div className="split-bottom" style={{ position: 'absolute', top: `calc(${mapHeight} + 8px)`, bottom: 0, left: 0, right: 0 }}>
        {bottomChild}
      </div>
    </div>
  );
};

export default ResizableSplitPanel;
